import { Action, Reducer } from 'redux';
import * as Actions from '../actions';
import * as ActionEvents from '../actions/PersonAddresses.Actions';
import { PersonAddressState, PersonAddressUnloadState } from '../states/PersonAddressState';
import { AddressViewModel } from '../../models/Entities/Person/Address/AddressModels';

export type KnownAction =
    | Actions.PersonAddresses.PersonAddressGetAllRequestAction
    | Actions.PersonAddresses.PersonAddressGetAllSuccessAction
    | Actions.PersonAddresses.PersonAddressGetAllFailureAction
    | Actions.PersonAddresses.PersonAddressGetByIdRequestAction
    | Actions.PersonAddresses.PersonAddressGetByIdSuccessAction
    | Actions.PersonAddresses.PersonAddressGetByIdFailureAction
    | Actions.PersonAddresses.PersonAddressCreateRequestAction
    | Actions.PersonAddresses.PersonAddressCreateSuccessAction
    | Actions.PersonAddresses.PersonAddressCreateFailureAction
    | Actions.PersonAddresses.PersonAddressUpdateRequestAction
    | Actions.PersonAddresses.PersonAddressUpdateSuccessAction
    | Actions.PersonAddresses.PersonAddressUpdateFailureAction
    | Actions.PersonAddresses.PersonAddressDeleteRequestAction
    | Actions.PersonAddresses.PersonAddressDeleteSuccessAction
    | Actions.PersonAddresses.PersonAddressDeleteFailureAction;

export const PersonAddressesReducer: Reducer<PersonAddressState> = (
    state: PersonAddressState | undefined,
    incomingAction: Action
): PersonAddressState => {
    if (state === undefined) {
        return PersonAddressUnloadState as PersonAddressState;
    }

    const action = incomingAction as KnownAction;

    switch (action.type) {
        case ActionEvents.PERSON_ADDRESS_GETALL_REQUEST:
            return {
                ...state,
                isLoadingAll: true,
                successLoadingAll: undefined,
                failOnLoadingAll: false,
                error: undefined
            };
        case ActionEvents.PERSON_ADDRESS_GETALL_SUCCESS: {
            const otherPersons = state.list.filter((item: AddressViewModel) => item.personId !== action.personId);
            return {
                ...state,
                isLoadingAll: false,
                successLoadingAll: true,
                failOnLoadingAll: false,
                list: [...otherPersons, ...action.payload],
                error: undefined
            };
        }
        case ActionEvents.PERSON_ADDRESS_GETALL_FAILURE:
            return {
                ...state,
                isLoadingAll: false,
                successLoadingAll: false,
                failOnLoadingAll: true,
                error: action.error
            };

        case ActionEvents.PERSON_ADDRESS_GETBYID_REQUEST:
            return {
                ...state,
                isLoadingOne: true,
                successLoadingOne: undefined,
                failOnLoadingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_ADDRESS_GETBYID_SUCCESS: {
            const filtered = state.list.filter((item: AddressViewModel) => item.id !== action.payload.id);
            return {
                ...state,
                isLoadingOne: false,
                successLoadingOne: true,
                failOnLoadingOne: false,
                list: [...filtered, action.payload],
                error: undefined
            };
        }
        case ActionEvents.PERSON_ADDRESS_GETBYID_FAILURE:
            return {
                ...state,
                isLoadingOne: false,
                successLoadingOne: false,
                failOnLoadingOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_ADDRESS_CREATE_REQUEST:
            return {
                ...state,
                isAddingNewOne: true,
                successAddingNewOne: undefined,
                failOnAddingNewOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_ADDRESS_CREATE_SUCCESS: {
            const createdList = state.list.some((item: AddressViewModel) => item.id === action.payload.id)
                ? state.list.map((item: AddressViewModel) => (item.id === action.payload.id ? action.payload : item))
                : [...state.list, action.payload];
            return {
                ...state,
                isAddingNewOne: false,
                successAddingNewOne: true,
                failOnAddingNewOne: false,
                lastCreated: action.payload,
                list: createdList,
                error: undefined
            };
        }
        case ActionEvents.PERSON_ADDRESS_CREATE_FAILURE:
            return {
                ...state,
                isAddingNewOne: false,
                successAddingNewOne: false,
                failOnAddingNewOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_ADDRESS_UPDATE_REQUEST:
            return {
                ...state,
                isUpdatingOne: true,
                successUpdatingOne: undefined,
                failOnUpdatingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_ADDRESS_UPDATE_SUCCESS: {
            const updatedList = state.list.some((item: AddressViewModel) => item.id === action.payload.id)
                ? state.list.map((item: AddressViewModel) => (item.id === action.payload.id ? { ...item, ...action.payload } : item))
                : [...state.list, action.payload];
            return {
                ...state,
                isUpdatingOne: false,
                successUpdatingOne: true,
                failOnUpdatingOne: false,
                list: updatedList,
                error: undefined
            };
        }
        case ActionEvents.PERSON_ADDRESS_UPDATE_FAILURE:
            return {
                ...state,
                isUpdatingOne: false,
                successUpdatingOne: false,
                failOnUpdatingOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_ADDRESS_DELETE_REQUEST:
            return {
                ...state,
                isDeletingOne: true,
                successDeletingOne: undefined,
                failOnDeletingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_ADDRESS_DELETE_SUCCESS:
            return {
                ...state,
                isDeletingOne: false,
                successDeletingOne: true,
                failOnDeletingOne: false,
                list: state.list.filter((item: AddressViewModel) => item.id !== action.id),
                error: undefined
            };
        case ActionEvents.PERSON_ADDRESS_DELETE_FAILURE:
            return {
                ...state,
                isDeletingOne: false,
                successDeletingOne: false,
                failOnDeletingOne: true,
                error: action.error
            };
        default:
            return state;
    }
};
